import i18n, { type BackendModule, type ReadCallback } from 'i18next';
import { initReactI18next } from 'react-i18next';

// Each locale (~50–170 KB of JSON) is dynamically imported so Vite emits it
// as its own chunk. Only the active language (+ the `en` fallback) is fetched
// at startup instead of bundling all five into the universal entry chunk.
const loaders: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  en: () => import('./en.json'),
  sv: () => import('./sv.json'),
  fi: () => import('./locales/fi.json'),
  no: () => import('./locales/no.json'),
  de: () => import('./locales/de.json'),
  fr: () => import('./locales/fr.json'),
  es: () => import('./locales/es.json'),
};

const dynamicImportBackend: BackendModule = {
  type: 'backend',
  init: () => {},
  read: (language: string, _namespace: string, callback: ReadCallback) => {
    const load = loaders[language];
    if (!load) {
      callback(null, {});
      return;
    }
    load().then(
      (mod) => callback(null, mod.default),
      (err) => callback(err as Error, null),
    );
  },
};

const SUPPORTED_LNGS = ['en', 'sv', 'fi', 'no', 'de', 'fr', 'es'] as const;

/**
 * Languages allowed to drive the WHOLE app's chrome — i.e. to be auto-selected
 * on boot and persisted as the saved preference. German is deliberately NOT
 * here: it's a *content edition* (the fully-German /germany landing + the
 * magazine's in-place DE toggle), not a main-site UI language. Persisting `de`
 * globally used to leak German into the main nav on the next visit — the magazine
 * toggle would pin `de`, then the top bar (which reads the global instance,
 * outside PublicLangScope) booted German. Excluding it here also self-heals any
 * visitor already pinned to `de`: their stale preference is ignored on boot and
 * never re-written. German still works in-session via `changeLanguage('de')`.
 */
export const BOOT_LNGS = ['en', 'sv', 'fi', 'no', 'fr', 'es'] as const;

/**
 * Resolve the initial UI language. Priority:
 *   1. The user's saved preference (if still supported).
 *   2. The first browser-preferred language whose base tag we support
 *      (e.g. "sv-SE" → "sv").
 *   3. The English fallback.
 *
 * Note: the app boots with English as the default — it calls this with NO
 * navigator languages (step 2 disabled), so a fresh visitor always starts in
 * English and only a saved choice overrides it. Browser detection remains here
 * as a pure, testable capability for any opt-in caller.
 *
 * Pure so it can be unit-tested without a DOM.
 */
export function resolveInitialLanguage(
  saved: string | null | undefined,
  navigatorLanguages: readonly string[] = [],
  supported: readonly string[] = SUPPORTED_LNGS,
): string {
  if (saved && supported.includes(saved)) return saved;
  // Browsers report Norwegian as Bokmål/Nynorsk (nb/nn); the app's locale is `no`.
  const aliases: Record<string, string> = { nb: 'no', nn: 'no' };
  for (const lang of navigatorLanguages) {
    const base0 = lang.toLowerCase().split('-')[0];
    const base = aliases[base0] ?? base0;
    if (supported.includes(base)) return base;
  }
  return 'en';
}

const savedLang = typeof window !== 'undefined' ? localStorage.getItem('beetlesense-lang') : null;
// English is the default. A saved, supported chrome-language preference wins, but
// we deliberately do NOT auto-detect the browser's language at boot — so every
// fresh visitor, and every shared link, opens in English. Switching is one click
// and the choice persists. German is never a boot language (see BOOT_LNGS), so it
// can't leak into the main nav.
const initialLang = resolveInitialLanguage(savedLang, [], BOOT_LNGS);

// Resolves once the initial language(s) have loaded; the entry point awaits
// this before the first render so `t()` never flashes raw keys.
export const i18nReady = i18n
  .use(dynamicImportBackend)
  .use(initReactI18next)
  .init({
    lng: initialLang,
    fallbackLng: 'en',
    supportedLngs: [...SUPPORTED_LNGS],
    ns: ['translation'],
    defaultNS: 'translation',
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  });

i18n.on('languageChanged', (lng) => {
  // Persist only chrome languages. Reading the German edition switches the
  // session to `de` for a consistent page, but must NOT pin the whole app to
  // German for the next visit (that's the nav-leak bug). `de` stays session-only.
  if ((BOOT_LNGS as readonly string[]).includes(lng)) {
    localStorage.setItem('beetlesense-lang', lng);
  }
  document.documentElement.lang = lng;
});

export default i18n;
